import { useMsal } from '@azure/msal-react';

import { logger } from '../../services/logger';
import { MsalInstanceManager } from '../../singleton';

export const useGetAccessToken = (isGraphRequest?: boolean) => {
  const { accounts, instance } = useMsal();

  const account = accounts[0];

  // WI-F2/F3: scopes are injected at runtime into the MsalInstanceManager from
  // the fetched TenantConfig — no longer imported statically from the app.
  const msalManager = MsalInstanceManager.getInstance();
  const tokenRequest = {
    scopes: isGraphRequest
      ? [msalManager.getPresenceScope()]
      : msalManager.getApiScopes(),
    account: account!,
    forceRefresh: false,
  };

  const getAccessToken = async (retryCount = 0): Promise<string | null> => {
    const MAX_RETRIES = 2;

    // Check if logout is already in progress
    if (msalManager.getLoggingOut()) {
      return null;
    }

    try {
      const tokenResponse = await instance.acquireTokenSilent(tokenRequest);
      return tokenResponse.accessToken;
    } catch (error: unknown) {
      // Check again if logout started during token acquisition
      if (msalManager.getLoggingOut()) {
        return null;
      }

      // Only logout on specific authentication errors, not transient network errors
      if (
        error instanceof Error &&
        error.message &&
        (error.message.includes('interaction_required') ||
          error.message.includes('login_required') ||
          error.message.includes('no_account') ||
          error.message.includes('consent_required'))
      ) {
        // These are auth errors that require re-authentication
        logger.info('Authentication required, redirecting to login', {
          origin: 'useGetAccessToken',
          error: error.message,
        });
        msalManager.setLoggingOut(true);
        instance.logoutRedirect();
        return null;
      }

      // For transient errors (network issues, etc.), retry before giving up
      if (retryCount < MAX_RETRIES) {
        logger.info(
          `Token acquisition failed, retrying (${retryCount + 1}/${MAX_RETRIES})`,
          {
            origin: 'useGetAccessToken',
            error: error instanceof Error ? error.message : String(error),
          }
        );
        // Wait before retry (exponential backoff)
        await new Promise((resolve) =>
          setTimeout(resolve, Math.pow(2, retryCount) * 1000)
        );
        return getAccessToken(retryCount + 1);
      }

      // After all retries failed, log error but don't force logout
      // The user might have temporary network issues
      logger.error(error as Error, {
        origin: 'useGetAccessToken',
        message: 'Token acquisition failed after retries',
      });
      return null;
    }
  };

  return { getAccessToken };
};
